<template>
  <tr :class="{ 'is-package-header': true, 'is-collapsed': isCollapsed }">
    <td class="align-middle p-2">
      <span
        class="collapse-trigger mr-2"
        :title="isCollapsed ? $t('ui.expand') : $t('ui.collapse')"
        @click="$emit('toggle-collapse', servicePackageID)">
        <i class="material-icons">{{ isCollapsed ? 'add' : 'remove' }}</i>
      </span>

      <d-badge
        theme="dark"
        :outline="true"
        class="mr-2 align-middle">
        {{ $t('service.isPackage') }}
      </d-badge>

      <span class="align-middle">{{ packageGroupName }}</span>

      <order-creator-service-package-calculator
        v-if="!readOnly && allowServicePackagesAdd"
        :packageData="servicePackageData"
        :group="packageGroup"
        :calculatedService="calculatedService"
        :productIndex="productIndex"
        :readOnly="readOnly"
        :orderSetUpInProgress="orderSetUpInProgress" />
    </td>

    <td class="align-middle pt-2">
     <validation-provider
        v-if="!readOnly"
        rules="required|min_value:1"
        :name="'service-package-amount-' + servicePackageID"
        v-slot="{ errors }">
        <d-input-group>
          <d-input
            :min="1"
            :state="errors.length ? 'invalid' : null"
            v-model="qty"
            @input="servicesFromPackagePricesUpdateDueQtyChange(servicePackageID)"
            type="number" />
          <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
        </d-input-group>
      </validation-provider>
      <template v-else>
        {{ qty }} {{ $t('ui.piecesAbbr') }}
      </template>

      <small
        v-if="!userIsCustomer && parseInt(qty, 10) != $parent.$parent.$parent.productsData[productIndex].count"
        class="text-danger cursor-pointer"
        @click="setServicePackageAmountToProductAmount()">
        {{ $t('ui.productsGenitive') }}: {{ $parent.$parent.$parent.productsData[productIndex].count }} {{ $t('ui.piecesAbbr') }}
      </small>
    </td>

    <td
      :hidden="isIndent && isOffer"
      class="align-middle pt-2">
      <strong>{{ totalsToUse.price }} {{ selectedCurrencySymbol }}</strong>
    </td>

    <td
      :hidden="isIndent && isOffer"
      class="align-middle pt-2">
      <strong>{{ totalsToUse.setupPrice }} {{ selectedCurrencySymbol }}</strong>
    </td>

    <td
      :hidden="isIndent && isOffer"
      class="align-middle pt-2">
      <template v-if="totalsToUse.totalDiscountedPrice !== totalsToUse.totalPrice">
        <strong class="text-primary" style="display: block; line-height: 1;">
          {{ totalsToUse.totalDiscountedPrice }} {{ selectedCurrencySymbol }}
        </strong>
        <del style="display: block; line-height: 1;">
          <small>{{ totalsToUse.totalPrice }} {{ selectedCurrencySymbol }}</small>
        </del>
      </template>
      <template v-else>
        <strong class="text-primary">
          {{ totalsToUse.totalPrice }} {{ selectedCurrencySymbol }}
        </strong>
      </template>
    </td>

    <td
      v-if="!readOnly"
      class="align-middle pt-2">
      <d-button
        size="sm"
        outline
        @click.prevent="removeServicePackage"
        theme="danger">
        {{ $t('ui.delete') }}
      </d-button>
    </td>
  </tr>
</template>

<script>
import AppConfig from './../../.env';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import OrderCreatorServicePackageCalculator from './OrderCreatorServicePackageCalculator.vue';

export default {
  name: 'order-creator-service-package',
  mixins: [
    CustomerPanel
  ],
  props: [
    'isIndent',
    'isOffer',
    'readOnly',
    'productIndex',
    'servicePackageID',
    'servicePackages',
    'servicesGroups',
    'packageServices',
    'orderSetUpInProgress',
    'totals',
    'selectedCurrencySymbol',
    'isCollapsed'
  ],
  components: {
    'order-creator-service-package-calculator': OrderCreatorServicePackageCalculator
  },
  computed: {
    allowServicePackagesAdd () {
      return !!AppConfig.additionalConfig[AppConfig.useConfig].allowServicePackagesAdd;
    },
    totalsToUse () {
      if (!this.totals) {
        return {
          price: '0.00',
          setupPrice: '0.00',
          totalPrice: '0.00',
          totalDiscountedPrice: '0.00'
        };
      }

      return this.totals;
    },
    servicePackageData () {
      let packageData = this.servicePackages.filter(pkg => pkg.id === this.servicePackageID);

      if (packageData && packageData[0]) {
        return packageData[0];
      }

      return {};
    },
    packageGroup () {
      let servicesGroups = this.servicesGroups || [];
      let foundedGroup = servicesGroups.filter(group => group.id === this.servicePackageData.group_id);

      if (foundedGroup && foundedGroup[0]) {
        return foundedGroup[0];
      }

      return null;
    },
    packageGroupName () {
      if (this.servicePackageData.group_name) {
        return this.servicePackageData.group_name;
      }

      if (this.packageGroup) {
        return this.packageGroup.name;
      }

      return '';
    },
    calculatedService () {
      let services = this.packageServices || [];

      if (!this.packageGroup || !this.packageGroup.has_stitch_calculator) {
        return null;
      }

      let calculatorIDs = (this.packageGroup.calculator_services || []).map(service => service.id ? service.id : service);
      let foundedService = null;

      for (let i = 0; i < services.length; i++) {
        if (!services[i].id || calculatorIDs.indexOf(services[i].id) > -1) {
          foundedService = services[i];
        }
      }

      return foundedService;
    }
  },
  data () {
    return {
      qty: null
    };
  },
  mounted () {
    this.qty = this.servicePackageData.quantity;
  },
  methods: {
    setServicePackageAmountToProductAmount () {
      this.qty = this.$parent.$parent.$parent.productsData[this.productIndex].count;
      this.servicesFromPackagePricesUpdateDueQtyChange();
    },
    servicesFromPackagePricesUpdateDueQtyChange () {
      this.$bus.$emit('order-update-service-qty-from-package', this.servicePackageID, this.qty);
      this.$bus.$emit('order-update-service-package-qty-in-product', {
        productIndex: this.productIndex,
        packageID: this.servicePackageData.id,
        qty: this.qty
      });
    },
    removeServicePackage () {
      if (this.servicePackageData.id) {
        this.$bus.$emit('order-remove-service-package-from-product', {
          productIndex: this.productIndex,
          packageID: this.servicePackageData.id
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.is-package-header > td {
  background: rgba(255, 180, 0, 0.25);
  border-top: 2px solid rgba(255, 180, 0, 1);
}

.is-package-header > td:first-child {
  border-left: 2px solid rgba(255, 180, 0, 1);
}

.is-package-header > td:last-child {
  border-right: 2px solid rgba(255, 180, 0, 1);
}

.is-package-header.is-collapsed > td {
  border-bottom: 2px solid rgba(255, 180, 0, 1);
}

.collapse-trigger {
  align-items: center;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(0, 0, 0, 0.35);
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  height: 22px;
  justify-content: center;
  vertical-align: middle;
  width: 22px;
}

.collapse-trigger:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: rgba(0, 0, 0, 0.6);
}

.collapse-trigger > .material-icons {
  font-size: 16px;
  line-height: 20px;
  position: relative;
  top: 0px;
}
</style>
